// Extraído de PlanCuotasModal (tab "Caja Chica", handleAsignarCajaChica) — 2026-09-25, cuando el punto
// de acceso pasó al kebab (⋮) de "Por pagar" en FlujoCajaV2 (ítem "Pagada por caja chica" →
// AsignarCajaChicaModal) y el tab se retiró del modal. Mismo comportamiento: INSERT en caja_chica_gastos
// (monto = monto_total, fecha = fecha_emision, concepto, pais, created_by con el id real del usuario —
// 10.104), 23505 = "ya está en caja chica", y UPDATE de estado_vinculacion con su error capturado.
//
// Solo capa caja chica (regla 10.39, dimensión financiera "quién pagó"): NO clasifica — no escribe
// tipo_contable ni cuenta_er (10.141/10.142) — y NO toca aplicaciones_saldo / MBF / movimientos_caja /
// facturas_sii_vinculaciones (bloqueo vigente de la Fase 3 del cierre de fuente única). Solo SII recibida:
// caja_chica_gastos admite factura_externa en su CHECK, pero ningún punto de acceso la escribe.
//
// Cuotas y caja chica no coexisten. Antes lo garantizaba el tab lockeado de PlanCuotasModal
// (destinoComprometido, con getPlanPagos); ahora el guard vive acá, con el MISMO dato (getPlanPagos:
// cualquier fila de plan_pagos_facturas de la factura), para que ninguna superficie pueda saltearlo.
//
// Devuelve SIEMPRE un resultado explícito; nunca lanza ni traga errores (10.177). La UI (toasts) queda
// fuera del util, igual que guardarFechaFactura:
//   { ok: true, gastoRegistrado: true, gastoId }             → gasto registrado + factura marcada.
//   { ok: false, gastoRegistrado: true, gastoId, error }     → gasto registrado pero falló el UPDATE de
//                                                              estado_vinculacion (la factura igual sale de
//                                                              "Por pagar": el gate es caja_chica_gastos).
//   { ok: false, gastoRegistrado: false, codigo, error }     → no se escribió nada.
// `codigo`: "sin_empleado" | "id_no_editable" | "no_sii_recibida" | "error_plan" | "tiene_plan" |
// "ya_en_caja_chica" | "error_insert".
import { getPlanPagos } from "./planCobros";
import { client } from "./supabase";

const noEscrito = (codigo, error) => ({ ok: false, gastoRegistrado: false, codigo, error });

/**
 * @param {Object} params
 * @param {Object} params.factura - la entry de "Por pagar" (SII recibida): id, folio, monto_total,
 *   fecha_emision, razon_social_emisor, es_externa, es_boleta.
 * @param {string} params.empleadoId - empleados.id (UUID) del dueño del fondo de caja chica.
 * @param {string} params.pais - "Chile" | "Perú".
 * @param {number} params.userId - usuarios.id del usuario logueado (autoría real, 10.104).
 */
export const asignarFacturaCajaChica = async ({ factura, empleadoId, pais, userId }) => {
  if (!empleadoId) return noEscrito("sin_empleado", "Seleccioná un empleado");
  // Mismo guard que idEsEditable de PlanCuotasModal: un id sintético (string) no es una fila real.
  if (typeof factura?.id !== "number") {
    return noEscrito("id_no_editable", "Esta entry no es editable: no corresponde a una factura real.");
  }
  if (factura.es_externa || factura.es_boleta) {
    return noEscrito("no_sii_recibida", "Solo una factura SII recibida puede asignarse a caja chica.");
  }

  let plan;
  try {
    plan = await getPlanPagos(factura.id);
  } catch (err) {
    return noEscrito("error_plan", `Error al verificar el plan de cuotas: ${err.message}`);
  }
  if (plan.length > 0) {
    return noEscrito("tiene_plan", "La factura tiene plan de cuotas: no puede pagarse por caja chica.");
  }

  let gasto;
  try {
    const { data, error: insertError } = await client
      .from("caja_chica_gastos")
      .insert({
        empleado_id: empleadoId,
        factura_tipo: "sii_recibida",
        factura_id: factura.id,
        monto: Number(factura.monto_total || 0),
        fecha: factura.fecha_emision,
        concepto: `Factura ${factura.folio || factura.id} - ${factura.razon_social_emisor || ""}`.trim(),
        pais,
        created_by: userId,
      })
      .select("id")
      .single();
    if (insertError) {
      // 23505 = UNIQUE(factura_tipo, factura_id): la factura ya está en un fondo de caja chica.
      if (insertError.code === "23505") {
        return noEscrito("ya_en_caja_chica", "Esta factura ya está vinculada a caja chica");
      }
      return noEscrito("error_insert", `Error al asignar: ${insertError.message}`);
    }
    gasto = data;
  } catch (err) {
    return noEscrito("error_insert", `Error al asignar: ${err.message}`);
  }

  // El gasto ya quedó en caja_chica_gastos. PostgREST devuelve {error} sin throw: sin este chequeo un
  // UPDATE fallido dejaba la factura 'pendiente' con un éxito en pantalla (caso factura 77). Un throw
  // de red acá también es resultado parcial, nunca "no se escribió nada".
  let updError;
  try {
    ({ error: updError } = await client
      .from("facturas_sii_recibidas")
      .update({ estado_vinculacion: "vinculada" })
      .eq("id", factura.id));
  } catch (err) {
    updError = err;
  }
  if (updError) {
    return {
      ok: false,
      gastoRegistrado: true,
      gastoId: gasto.id,
      error: "Gasto registrado, pero no se pudo marcar la factura como vinculada. Reintentá.",
    };
  }
  return { ok: true, gastoRegistrado: true, gastoId: gasto.id };
};
